import { lazy, Suspense, useEffect, useRef, useState } from 'react'

const loadContactForm = () => import('./contact-form')
const ContactForm = lazy(() => loadContactForm().then((module) => ({ default: module.ContactForm })))

/**
 * Start the chunk and resolve with the form's first real field once it is in the DOM — the landing's CTAs
 * scroll here and put the cursor in it, and until the mount this section is about has rendered there is no
 * field to put it in. Resolves `null` if it never arrives, so a caller cannot hang on a chunk that failed.
 */
export function contactFieldReady(section: Element, timeoutMs = 4000): Promise<HTMLInputElement | null> {
  void loadContactForm()
  // The honeypot is the form's first input and takes focus perfectly happily, which is why it is excluded.
  const find = () => section.querySelector<HTMLInputElement>('input:not([tabindex="-1"])')
  const present = find()
  if (present) return Promise.resolve(present)
  return new Promise((resolve) => {
    const observer = new MutationObserver(() => {
      const field = find()
      if (!field) return
      clearTimeout(timer)
      observer.disconnect()
      resolve(field)
    })
    const timer = setTimeout(() => {
      observer.disconnect()
      resolve(null)
    }, timeoutMs)
    observer.observe(section, { childList: true, subtree: true })
  })
}

/**
 * The height the form settles at — measured 2026-09-23 at 390, 768 and 1280px wide, where it is 344px at all
 * three because nothing in it wraps. Reserved below so the mount moves nothing; `contact-form.e2e` fails if
 * the form outgrows it.
 */
export const CONTACT_FORM_HEIGHT = 344

/**
 * The landing's only form, and mounting it drags the whole field set in with it (`use-app-form`), which a
 * visitor who never reaches the foot of the page should not download. Held until the section is a screen away,
 * so the chunk normally lands before the box is on screen and the swap costs no layout shift; the reserved
 * height covers the case it does not — a `/#contact` arrival, where the box is already under the reader.
 */
export function ContactFormOnAppear(props: { compact?: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node || mounted) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        observer.disconnect()
        setMounted(true)
      },
      { rootMargin: '600px 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [mounted])

  return (
    <div ref={ref} style={{ minHeight: CONTACT_FORM_HEIGHT }}>
      {mounted && (
        <Suspense fallback={null}>
          <ContactForm compact={props.compact} />
        </Suspense>
      )}
    </div>
  )
}
